import type { HTMLAttributes, ReactNode } from 'react';
import { Spinner } from '../Spinner/index.js';
import { Skeleton } from '../Skeleton/index.js';
import { useI18nT } from '../../i18n/index.js';

export interface LoadingProps extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
  /** `spinner` (default) centers a spinner + label; `skeleton` renders
   *  placeholder blocks instead for feed-like views. Custom nodes pass
   *  through as a fourth variant. */
  variant?: 'spinner' | 'skeleton';
  /** Rows to render in skeleton mode. Default 3. */
  rows?: number;
  /** Label under the spinner. Defaults to the locale's "Loading" string. */
  label?: ReactNode;
  /** Optional child content overrides the default variant entirely. */
  children?: ReactNode;
  className?: string;
}

/** Canonical loading block. Use inside `<DataView>` or standalone around
 *  async panels. Distinct from `<Spinner>` because it composes an a11y
 *  label + a block-level slot where a spinner alone would be too small. */
export function Loading(props: LoadingProps): ReactNode {
  const { variant = 'spinner', rows = 3, label, children, className, ...rest } = props;
  const t = useI18nT();
  const classes = ['vq-loading', className].filter(Boolean).join(' ');
  const resolvedLabel = label ?? t('common.loading');
  return (
    <div
      {...rest}
      className={classes}
      data-component="Loading"
      data-variant={variant}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {children !== undefined ? (
        children
      ) : variant === 'skeleton' ? (
        <Skeleton lines={rows} />
      ) : (
        <>
          <Spinner size="md" />
          <span className="vq-loading__label">{resolvedLabel}</span>
        </>
      )}
    </div>
  );
}
